import React, { Component, Fragment } from 'react';
import { Layout, Typography, Button, Row, Col, Card, Table, Pagination, Input, Space, message, Modal } from 'antd';
import { SearchOutlined, PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import AdminPortalHeader from '../../components/common/admin_portal_header';
import { getVetsForAdmin, deleteVetForAdmin } from '../../api/pets-api';

const { Paragraph, Title } = Typography;
const { Content } = Layout;
const { Search } = Input;

class AdminVets extends Component {
    constructor(props) {
        super(props);
        this.state = {
            vets: [],
            currentPage: 1,
            pageSize: 20,
            totalRecs: 0,
            searchText: '',
            loading: false,
        };
    }

    getVets = async () => {
        this.setState({ loading: true });
        try {
            const params = {
                page: this.state.currentPage,
                pageSize: this.state.pageSize,
            };

            // Add optional search filter
            if (this.state.searchText) {
                params.search = this.state.searchText;
            }

            let respo = await getVetsForAdmin(params);
            console.log(respo);

            if (respo.status === 200 && respo.data.status === 'ok') {
                const { vets, total } = respo.data.result;
                this.setState({
                    vets: vets || [],
                    totalRecs: total || 0,
                });
            } else {
                this.setState({ vets: [], totalRecs: 0 });
            }
        } catch (ex) {
            console.error('Error fetching vets:', ex);
            this.setState({ vets: [], totalRecs: 0 });
            message.error('Error loading vets');
        } finally {
            this.setState({ loading: false });
        }
    };

    onPaginationChange = async (pageNumber) => {
        await this.setState({ currentPage: pageNumber });
        await this.getVets();
    };

    showTotal = (total) => {
        return `Total ${total} Records`;
    };

    onSearch = async (value) => {
        await this.setState({ searchText: value, currentPage: 1 });
        await this.getVets();
    };

    onClearFilters = async () => {
        await this.setState({
            searchText: '',
            currentPage: 1,
        });
        await this.getVets();
    };

    async componentDidMount() {
        await this.getVets();
    }

    navigateToVet(vetId) {
        console.log('Vet ID', vetId);
        window.location.pathname = '/admin_vets/' + vetId;
    }

    navigateToCreateVet = () => {
        window.location.pathname = '/admin_vets/new';
    };

    handleDeleteVet = (vetId, vetName) => {
        Modal.confirm({
            title: 'Delete Vet',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to delete ${vetName}? This action cannot be undone.`,
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const response = await deleteVetForAdmin(vetId);
                    if (response.status === 200) {
                        message.success('Vet deleted successfully');
                        await this.getVets();
                    } else {
                        message.error('Failed to delete vet');
                    }
                } catch (error) {
                    console.error('Error deleting vet:', error);
                    if (error.response && error.response.status === 404) {
                        message.error('Vet not found');
                    } else {
                        message.error('Error deleting vet');
                    }
                }
            },
        });
    };

    render() {
        const columns = [
            {
                title: 'Clinic Name',
                dataIndex: 'clinicName',
                render: (clinicName) => (
                    <Fragment key={1}>
                        <Paragraph className="text-muted p-0 mb-0">{clinicName || 'N/A'}</Paragraph>
                    </Fragment>
                ),
            },
            {
                title: 'Vet Name',
                dataIndex: 'firstName',
                render: (firstName, record) => (
                    <Fragment key={2}>
                        <Paragraph className="text-muted p-0 mb-0">
                            {firstName && record.lastName
                                ? `${firstName} ${record.lastName}`
                                : firstName || record.lastName || 'N/A'}
                        </Paragraph>
                    </Fragment>
                ),
            },
            {
                title: 'Email',
                dataIndex: 'emailAddress',
                render: (emailAddress) => (
                    <Fragment key={3}>
                        <Paragraph className="text-muted p-0 mb-0">{emailAddress || 'N/A'}</Paragraph>
                    </Fragment>
                ),
            },
            {
                title: 'Phone',
                dataIndex: 'phone',
                render: (phone) => (
                    <Fragment key={4}>
                        <Paragraph className="text-muted p-0 mb-0">{phone || 'N/A'}</Paragraph>
                    </Fragment>
                ),
            },
            {
                title: 'Area',
                dataIndex: 'area',
                render: (area) => (
                    <Fragment key={5}>
                        <Paragraph className="text-muted p-0 mb-0">{area || 'N/A'}</Paragraph>
                    </Fragment>
                ),
            },
            {
                title: 'Actions',
                dataIndex: 'id',
                render: (id, record) => (
                    <Fragment key={6}>
                        <Space>
                            <Button
                                htmlType="button"
                                type="primary"
                                size="small"
                                onClick={() => this.navigateToVet(id)}
                            >
                                View/Edit
                            </Button>
                            <Button
                                htmlType="button"
                                type="primary"
                                danger
                                size="small"
                                onClick={() =>
                                    this.handleDeleteVet(
                                        id,
                                        record.clinicName || `${record.firstName} ${record.lastName}`
                                    )
                                }
                            >
                                Delete
                            </Button>
                        </Space>
                    </Fragment>
                ),
            },
        ];

        return (
            <Layout className="dashboard vets">
                <AdminPortalHeader />
                <div className="m-2">
                    <Row justify="space-between" align="middle" className="pt-3 px-3">
                        <Col>
                            <Title level={2} className="text-green font-600 m-0">
                                Vets Management
                            </Title>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={this.navigateToCreateVet}
                                size="large"
                            >
                                Add New Vet
                            </Button>
                        </Col>
                    </Row>
                    <Row gutter={15}>
                        <Col className="pt-3 px-3" xs={6} sm={6} md={6} lg={6} xl={6}>
                            <div className="filters-section">
                                <Title level={5}>Search</Title>

                                <div className="mb-3">
                                    <label>Search by Clinic, Name, Email, Phone, or Area:</label>
                                    <Search
                                        placeholder="Enter search text"
                                        allowClear
                                        enterButton="Search"
                                        value={this.state.searchText}
                                        onChange={(e) => this.setState({ searchText: e.target.value })}
                                        onSearch={this.onSearch}
                                    />
                                </div>

                                <Button
                                    htmlType="button"
                                    className="full-width mt-3"
                                    onClick={this.onClearFilters}
                                >
                                    Clear Search
                                </Button>
                            </div>
                        </Col>
                        <Col className="pt-3 px-3" xs={18} sm={18} md={18} lg={18} xl={18}>
                            <Card
                                className="custom-shadow green"
                                title={
                                    <Title level={4} className="text-center text-white">
                                        Vets List
                                    </Title>
                                }
                            >
                                <div>
                                    <Table
                                        className="noborder"
                                        columns={columns}
                                        dataSource={this.state.vets}
                                        pagination={false}
                                        size="small"
                                        loading={this.state.loading}
                                        rowKey="id"
                                    />

                                    {this.state.totalRecs > 0 && (
                                        <div>
                                            <hr></hr>
                                            <Pagination
                                                current={this.state.currentPage}
                                                pageSize={this.state.pageSize}
                                                total={this.state.totalRecs}
                                                onChange={async (page) => await this.onPaginationChange(page)}
                                                showTotal={this.showTotal}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Layout>
        );
    }
}

export default AdminVets;
